import type { TeamMember } from './TeamTabContext';

export function activeProductIdsFromPerson(person: Record<string, unknown>): string[] {
  const slices = Array.isArray(person.products) ? person.products : [];
  const ids = slices
    .filter((s: Record<string, unknown>) => s.isActive !== false)
    .map((s: Record<string, unknown>) =>
      typeof s.productId === 'string' ? s.productId : s.productId != null ? String(s.productId) : ''
    )
    .filter(Boolean);
  return Array.from(new Set(ids));
}

export function rosterCheckboxTitlesForPerson(
  person: Record<string, unknown>
): Record<string, string> {
  const slices = Array.isArray(person.products) ? person.products : [];
  const out: Record<string, string> = {};
  for (const raw of slices) {
    const s = raw as Record<string, unknown>;
    const pid =
      typeof s.productId === 'string'
        ? s.productId
        : s.productId != null
          ? String(s.productId)
          : '';
    if (!pid) continue;
    const src = String(s.source || '');
    const tag = src === 'manual' ? 'manual' : src === 'worklog_sync' ? 'auto' : src ? src : '';
    out[pid] = tag
      ? `Pairing source: ${tag}. Manual edits here override Jira worklog sync.`
      : 'Manual edits here override Jira worklog sync.';
  }
  return out;
}

export function teamMemberRowForPerson(
  personsTeamSlice: TeamMember[],
  person: Record<string, unknown>
): TeamMember | undefined {
  const personUuid =
    typeof person.id === 'string' ? person.id : person.id != null ? String(person.id) : '';
  if (!personUuid) return undefined;
  return personsTeamSlice.find((m) => m.personId === personUuid);
}

/**
 * Jira username shown on the roster row. Empty string on the product member
 * must not fall through to `person.jira_username` (`??` would, `typeof` does not).
 */
export function rosterPersonJiraUsername(
  person: Record<string, unknown>,
  member: TeamMember | undefined
): string {
  const fromMember = member?.jiraUsername;
  if (typeof fromMember === 'string') return fromMember;
  return typeof person.jira_username === 'string' ? person.jira_username : '';
}

export function isRosterPersonJiraLinked(
  person: Record<string, unknown>,
  member: TeamMember | undefined
): boolean {
  return rosterPersonJiraUsername(person, member).trim() !== '';
}

/** Empty `selectedRoles` = all people, including those with no current-product row. */
export function personMatchesRosterRoleFilter(
  member: TeamMember | undefined,
  selectedRoles: readonly string[]
): boolean {
  if (selectedRoles.length === 0) return true;
  const role = member?.role;
  return typeof role === 'string' && role !== '' && selectedRoles.includes(role);
}

function rosterPersonMatchesSearch(
  person: Record<string, unknown>,
  member: TeamMember | undefined,
  search: string
): boolean {
  const q = search.toLowerCase().trim();
  if (!q) return true;
  const personName = typeof person.display_name === 'string' ? person.display_name : '';
  const personJira = typeof person.jira_username === 'string' ? person.jira_username : '';
  const personEmail = typeof person.email === 'string' ? person.email : '';
  return [
    personName,
    personJira,
    personEmail,
    member?.name,
    member?.email,
    member?.jiraUsername,
    member?.role,
  ].some((f) => typeof f === 'string' && f.toLowerCase().includes(q));
}

export function filterRosterPersons(
  persons: Record<string, unknown>[],
  personsTeamSlice: TeamMember[],
  options: {
    hideDeactivated: boolean;
    selectedRoles: readonly string[];
    search: string;
  }
): Record<string, unknown>[] {
  return persons.filter((person) => {
    const member = teamMemberRowForPerson(personsTeamSlice, person);
    if (options.hideDeactivated && !isRosterPersonJiraLinked(person, member)) return false;
    if (!personMatchesRosterRoleFilter(member, options.selectedRoles)) return false;
    return rosterPersonMatchesSearch(person, member, options.search);
  });
}

/** Snapshot for Team roster "Save Capacity Settings" diff (excludes distributionCriteria — separate save). */
export function rosterCapacitySnapshot(member: TeamMember): string {
  return JSON.stringify({
    name: (member.name || '').trim(),
    email: (member.email || '').trim(),
    jiraUsername: (member.jiraUsername || '').trim(),
    capacityHoursPerDay: member.capacityHoursPerDay ?? 8,
    includeInCapacity: member.includeInCapacity === true || member.includeInCapacity === undefined,
    includeInHistoricalCapacity: member.includeInHistoricalCapacity === true,
    excludeFromDistribution: member.excludeFromDistribution === true,
    startDateInTeam: member.startDateInTeam ?? '',
    endDateInTeam: member.endDateInTeam ?? '',
    role: member.role || 'developer',
    isActive: member.isActive !== false,
  });
}

export function serializeDistributionCriteriaForRoster(teamMember: TeamMember): string | null {
  const dc = teamMember.distributionCriteria;
  if (!dc || typeof dc !== 'object') return null;
  const cfRaw = dc.customFields;
  const parsedCf =
    cfRaw != null && typeof cfRaw === 'object' && !Array.isArray(cfRaw)
      ? (cfRaw as Record<string, unknown>)
      : {};
  const keysOk = Object.keys(parsedCf).every((k) => typeof k === 'string');
  return JSON.stringify({
    cluster: Array.isArray(dc.cluster) ? dc.cluster : [],
    component: Array.isArray(dc.component) ? dc.component : [],
    label: Array.isArray(dc.label) ? dc.label : [],
    priority: Array.isArray(dc.priority) ? dc.priority : [],
    customFields: keysOk
      ? Object.fromEntries(
          Object.entries(parsedCf).map(([k, v]) => [
            k,
            Array.isArray(v) ? v.filter((x) => typeof x === 'string') : [],
          ])
        )
      : {},
  });
}
